// src/components/condos/WaitlistModal.tsx
// Per docs/2026-01-25-condo-pairing-system/waitlist-modal-design.md

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Condo,
  WaitlistUser,
  GetWaitlistUsersResponse,
} from '../../types/condo';
import Avatar from '../common/Avatar';
import { discoverAPI } from '../../services/discoverService';
import MatchInvitationModal from '../common/MatchInvitationModal';

interface WaitlistModalProps {
  condoId: string;
  condo: Condo | null;
  condoName: string;
  isOpen: boolean;
  onClose: () => void;
  onFetchWaitlistUsers: (condoId: string) => Promise<{ success: boolean; data?: GetWaitlistUsersResponse; error?: string }>;
  onJoinWaitlist: (condoId: string) => Promise<{ success: boolean; error?: string }>;
  onLeaveWaitlist: (condoId: string) => Promise<{ success: boolean; error?: string }>;
  onRefresh?: () => void;
}

const WaitlistModal: React.FC<WaitlistModalProps> = ({
  condoId,
  condo,
  condoName,
  isOpen,
  onClose,
  onFetchWaitlistUsers,
  onJoinWaitlist,
  onLeaveWaitlist,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<WaitlistUser[]>([]);
  const [currentUserOnWaitlist, setCurrentUserOnWaitlist] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [joinLoading, setJoinLoading] = useState(false);
  const [leaveLoading, setLeaveLoading] = useState(false);
  const [invitationTargetUserId, setInvitationTargetUserId] = useState<string | null>(null);
  const [invitationLoading, setInvitationLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!condoId || !isOpen) return;
    setLoading(true);
    setError(null);
    try {
      const result = await onFetchWaitlistUsers(condoId);
      if (result.success && result.data) {
        setUsers(result.data.users ?? []);
        setCurrentUserOnWaitlist(result.data.currentUserOnWaitlist ?? false);
      } else {
        setError(result.error ?? 'Failed to load waitlist');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [condoId, isOpen, onFetchWaitlistUsers]);

  useEffect(() => {
    if (isOpen && condoId) {
      fetchData();
    }
  }, [isOpen, condoId, fetchData]);

  const handleJoin = async () => {
    setJoinLoading(true);
    try {
      const result = await onJoinWaitlist(condoId);
      if (result.success) {
        await fetchData();
        onRefresh?.();
      } else {
        alert(result.error ?? 'Failed to join waitlist');
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setJoinLoading(false);
    }
  };

  const handleLeave = async () => {
    setLeaveLoading(true);
    try {
      const result = await onLeaveWaitlist(condoId);
      if (result.success) {
        await fetchData();
        onRefresh?.();
      } else {
        alert(result.error ?? 'Failed to leave waitlist');
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setLeaveLoading(false);
    }
  };

  const handleSendInvitation = (targetUserId: string) => () => {
    setInvitationTargetUserId(targetUserId);
  };

  const handleInvitationSubmit = async (message: string) => {
    if (!invitationTargetUserId) return;
    setInvitationLoading(true);
    try {
      const result = await discoverAPI.sendMatchInvitation(invitationTargetUserId, message || undefined);
      if (result.success) {
        setInvitationTargetUserId(null);
        await fetchData();
      } else {
        alert(result.message ?? 'Failed to send invitation');
      }
    } catch (err) {
      alert((err as Error).message);
    } finally {
      setInvitationLoading(false);
    }
  };

  const handleViewProfile = (userId: string) => {
    onClose();
    navigate(`/profile/view/${userId}`);
  };

  const formatRent = (w: WaitlistUser) => {
    const min = w.housingInfo?.rentPreferences?.minRent;
    const max = w.housingInfo?.rentPreferences?.maxRent;
    if (min != null && max != null) return `$${min.toLocaleString()} – $${max.toLocaleString()}`;
    if (min != null) return `$${min.toLocaleString()}+`;
    if (max != null) return `Up to $${max.toLocaleString()}`;
    return '—';
  };

  const formatMoveIn = (w: WaitlistUser) => {
    const d = w.housingInfo?.moveInDate;
    if (!d) return '—';
    try {
      const date = new Date(d);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch {
      return d;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-cream border-2 border-coffee rounded-xl shadow-[4px_4px_0px_#594A42] max-w-lg w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b-2 border-coffee">
          <h2 className="text-coffee font-bold text-xl">Interest Group — {condoName}</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-coffee hover:text-terracotta text-2xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-4 overflow-y-auto flex-1">
          {loading && (
            <div className="text-center py-8 text-coffee">Loading...</div>
          )}

          {error && (
            <div className="text-red-600 mb-4">{error}</div>
          )}

          {!loading && !error && (
            <>
              {/* You row (when on waitlist) */}
              {currentUserOnWaitlist && (
                <div className="bg-white border-2 border-coffee rounded-lg p-4 mb-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-terracotta/30 flex items-center justify-center text-coffee font-bold">
                        You
                      </div>
                      <div>
                        <p className="text-coffee font-semibold">You (current user)</p>
                        <p className="text-coffee/70 text-xs">✓ You&apos;re in the interest group</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={handleLeave}
                      disabled={leaveLoading}
                      className="bg-white border-2 border-coffee text-coffee px-3 py-1 rounded-lg text-sm hover:bg-cream disabled:opacity-50"
                    >
                      {leaveLoading ? 'Leaving...' : 'Leave'}
                    </button>
                  </div>
                </div>
              )}

              {/* Empty state (when no other users) */}
              {users.length === 0 && !currentUserOnWaitlist && (
                <p className="text-coffee mb-4">No one in the interest group yet. Be the first!</p>
              )}

              {/* User rows */}
              {users.map((w) => (
                <div key={w.userId} className="bg-white border-2 border-coffee rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <Avatar
                      profilePictureUrl={w.personalInfo?.profilePicture ?? null}
                      firstName={w.personalInfo?.firstName}
                      lastName={w.personalInfo?.lastName}
                      userId={w.userId}
                      size="medium"
                      containerClassName="border-2 border-coffee flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-coffee font-semibold">
                        {w.personalInfo?.firstName ?? ''} {w.personalInfo?.lastName ?? '—'}
                      </p>
                      <p className="text-coffee/80 text-sm">
                        {w.personalInfo?.sex ?? '—'}, {w.personalInfo?.age ?? '—'}
                      </p>
                      <p className="text-coffee/80 text-sm">
                        Start: {formatMoveIn(w)} · Rent: {formatRent(w)}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {w.hasConversation ? (
                          <span className="bg-coffee/20 text-coffee px-2 py-1 rounded text-sm">Already connected</span>
                        ) : w.isPending ? (
                          <span className="bg-coffee/20 text-coffee px-2 py-1 rounded text-sm">Pending Invitation</span>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendInvitation(w.userId)}
                            className="bg-terracotta text-white border-2 border-coffee px-2 py-1 rounded text-sm hover:bg-[#d89050]"
                          >
                            Send Invitation
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleViewProfile(w.userId)}
                          className="bg-cream border-2 border-coffee text-coffee px-2 py-1 rounded text-sm hover:bg-white"
                        >
                          View Profile
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {/* Join waitlist button (when not on waitlist) */}
              {!currentUserOnWaitlist && (
                <div className="border-2 border-dashed border-coffee rounded-lg p-4">
                  <button
                    type="button"
                    onClick={handleJoin}
                    disabled={joinLoading}
                    className="w-full text-coffee font-medium hover:text-terracotta disabled:opacity-50"
                  >
                    {joinLoading ? 'Joining...' : '+ Join Interest Group'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Match Invitation Modal */}
      <MatchInvitationModal
        isOpen={!!invitationTargetUserId}
        onClose={() => setInvitationTargetUserId(null)}
        onSendInvitation={(msg) => {
          if (invitationTargetUserId) handleInvitationSubmit(msg);
        }}
        loading={invitationLoading}
      />
    </div>
  );
};

export default WaitlistModal;
